/**
 * SICH Wishlist — Loro Piana style
 * ============================================================================
 * Sistema de favoritos sin login basado en localStorage.
 *
 * Arquitectura:
 *   - SichWishlistStore: única fuente de verdad (singleton, expuesto en window.sichWishlist).
 *     Persiste a localStorage y dispara eventos cuando cambia.
 *   - <sich-wishlist-bookmark>: botón de favorito en cada PLP card.
 *   - <sich-wishlist-trigger>: ícono del header con contador.
 *   - <sich-wishlist-drawer>: drawer lateral derecho con la lista de favoritos.
 *
 * Eventos (broadcast en `document`):
 *   - sich:wishlist:change  { count, items }   — cualquier modificación del store
 *   - sich:wishlist:open                       — solicitud de abrir drawer
 *   - sich:wishlist:close                      — solicitud de cerrar drawer
 *
 * Endpoints Shopify usados:
 *   - GET  /products/{handle}.js   — info actualizada de producto + variants
 *   - POST /cart/add.js            — agregar al carrito
 *
 * El script es idempotente: se puede cargar varias veces sin duplicar listeners
 * ni redefinir custom elements.
 * ============================================================================
 */
(function () {
  'use strict';

  if (window.__sichWishlistInitialized) return;
  window.__sichWishlistInitialized = true;

  /* --------------------------------------------------------------------------
   * Constantes
   * ------------------------------------------------------------------------ */
  var STORAGE_KEY  = 'sich_wishlist_v1';
  var EVENT_CHANGE = 'sich:wishlist:change';
  var EVENT_OPEN   = 'sich:wishlist:open';
  var EVENT_CLOSE  = 'sich:wishlist:close';

  /* --------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------ */
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatPriceCents(cents) {
    // Shopify devuelve precios en centavos. AR usa punto como separador de miles.
    if (cents == null) return '';
    var amount = Math.round(cents / 100);
    var withSeparators = String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return '$' + withSeparators;
  }

  /* --------------------------------------------------------------------------
   * Store — estado en localStorage
   * ------------------------------------------------------------------------ */
  function SichWishlistStore() {
    this.items = this._load();
  }

  SichWishlistStore.prototype._load = function () {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  };

  SichWishlistStore.prototype._save = function () {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    } catch (e) {
      // Storage lleno, en modo privado, etc. — no romper la app.
      if (window.console && console.warn) console.warn('SICH Wishlist: storage save failed', e);
    }
    this._dispatch();
  };

  SichWishlistStore.prototype._dispatch = function () {
    document.dispatchEvent(new CustomEvent(EVENT_CHANGE, {
      detail: { count: this.count(), items: this.items.slice() }
    }));
  };

  SichWishlistStore.prototype.has = function (handle) {
    if (!handle) return false;
    for (var i = 0; i < this.items.length; i++) {
      if (this.items[i].handle === handle) return true;
    }
    return false;
  };

  SichWishlistStore.prototype.add = function (handle, meta) {
    if (!handle || this.has(handle)) return false;
    var item = { handle: handle, addedAt: Date.now() };
    if (meta && typeof meta === 'object') {
      if (meta.tejido)    item.tejido    = meta.tejido;
      if (meta.variantId) item.variantId = meta.variantId;
    }
    this.items.unshift(item);
    this._save();
    return true;
  };

  SichWishlistStore.prototype.remove = function (handle) {
    if (!handle) return false;
    var before = this.items.length;
    this.items = this.items.filter(function (item) { return item.handle !== handle; });
    if (this.items.length === before) return false;
    this._save();
    return true;
  };

  SichWishlistStore.prototype.toggle = function (handle, meta) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle, meta);
    return true;
  };

  SichWishlistStore.prototype.count   = function () { return this.items.length; };
  SichWishlistStore.prototype.getAll  = function () { return this.items.slice(); };

  // Singleton expuesto globalmente
  var store = new SichWishlistStore();
  window.sichWishlist = store;
  window.sichWishlist.openDrawer  = function () { document.dispatchEvent(new CustomEvent(EVENT_OPEN)); };
  window.sichWishlist.closeDrawer = function () { document.dispatchEvent(new CustomEvent(EVENT_CLOSE)); };

  /* --------------------------------------------------------------------------
   * <sich-wishlist-bookmark> — bookmark en cada card
   * ------------------------------------------------------------------------ */
  var SichWishlistBookmark = function () {
    var Cls = function () { return Reflect.construct(HTMLElement, [], Cls); };
    Cls.prototype = Object.create(HTMLElement.prototype);
    Cls.prototype.constructor = Cls;
    Object.setPrototypeOf(Cls, HTMLElement);

    Cls.prototype.connectedCallback = function () {
      this._handleClick = this._handleClick.bind(this);
      this._handleStoreChange = this._sync.bind(this);

      var btn = this.querySelector('button');
      if (btn) btn.addEventListener('click', this._handleClick);
      document.addEventListener(EVENT_CHANGE, this._handleStoreChange);
      this._sync();
    };

    Cls.prototype.disconnectedCallback = function () {
      var btn = this.querySelector('button');
      if (btn && this._handleClick) btn.removeEventListener('click', this._handleClick);
      if (this._handleStoreChange) document.removeEventListener(EVENT_CHANGE, this._handleStoreChange);
    };

    Cls.prototype._handleClick = function (e) {
      e.preventDefault();
      e.stopPropagation();

      var handle = this.getAttribute('product-handle');
      if (!handle) return;

      // Si la card tiene un swatch activo, guardar el tejido.
      var card = this.closest('sich-product-card');
      var meta = {};
      if (card) {
        var activeSwatch = card.querySelector('.sich-card__swatch.is-active');
        if (!activeSwatch) {
          // Default: primer swatch que no sea coming-soon
          activeSwatch = card.querySelector('.sich-card__swatch:not(.sich-card__swatch--coming-soon)');
        }
        if (activeSwatch) {
          var tejido = activeSwatch.getAttribute('data-option-value');
          if (tejido) meta.tejido = tejido;
        }
      }

      store.toggle(handle, meta);
      this._sync();

      // Animación de pulse
      this.classList.remove('is-pulsing');
      // forzar reflow para reiniciar animación
      void this.offsetWidth;
      this.classList.add('is-pulsing');
    };

    Cls.prototype._sync = function () {
      var handle = this.getAttribute('product-handle');
      var inWishlist = store.has(handle);
      this.classList.toggle('is-active', inWishlist);
      var btn = this.querySelector('button');
      if (btn) {
        btn.setAttribute('aria-pressed', inWishlist ? 'true' : 'false');
        btn.setAttribute('aria-label', inWishlist
          ? 'Quitar de favoritos'
          : 'Agregar a favoritos');
      }
    };

    return Cls;
  }();

  /* --------------------------------------------------------------------------
   * <sich-wishlist-trigger> — ícono del header con contador
   * ------------------------------------------------------------------------ */
  var SichWishlistTrigger = function () {
    var Cls = function () { return Reflect.construct(HTMLElement, [], Cls); };
    Cls.prototype = Object.create(HTMLElement.prototype);
    Cls.prototype.constructor = Cls;
    Object.setPrototypeOf(Cls, HTMLElement);

    Cls.prototype.connectedCallback = function () {
      this._handleClick = this._handleClick.bind(this);
      this._handleStoreChange = this._sync.bind(this);

      var btn = this.querySelector('button');
      if (btn) btn.addEventListener('click', this._handleClick);
      document.addEventListener(EVENT_CHANGE, this._handleStoreChange);
      this._sync();
    };

    Cls.prototype.disconnectedCallback = function () {
      var btn = this.querySelector('button');
      if (btn && this._handleClick) btn.removeEventListener('click', this._handleClick);
      if (this._handleStoreChange) document.removeEventListener(EVENT_CHANGE, this._handleStoreChange);
    };

    Cls.prototype._handleClick = function (e) {
      e.preventDefault();
      window.sichWishlist.openDrawer();
    };

    Cls.prototype._sync = function () {
      var count = store.count();
      var counter = this.querySelector('.sich-wishlist-trigger__count');
      if (counter) {
        counter.textContent = String(count);
        counter.classList.toggle('is-visible', count > 0);
      }
      this.classList.toggle('has-items', count > 0);
    };

    return Cls;
  }();

  /* --------------------------------------------------------------------------
   * <sich-wishlist-drawer> — drawer lateral con lista
   * ------------------------------------------------------------------------ */
  var SichWishlistDrawer = function () {
    var Cls = function () { return Reflect.construct(HTMLElement, [], Cls); };
    Cls.prototype = Object.create(HTMLElement.prototype);
    Cls.prototype.constructor = Cls;
    Object.setPrototypeOf(Cls, HTMLElement);

    Cls.prototype.connectedCallback = function () {
      this._isOpen = false;
      this._fetchCache = new Map();
      this._renderToken = 0; // para abortar renders viejos

      this._handleStoreChange = this._handleStoreChange.bind(this);
      this._handleOpen        = this._handleOpen.bind(this);
      this._handleClose       = this._handleClose.bind(this);
      this._handleEscape      = this._handleEscape.bind(this);
      this._handleClickInside = this._handleClickInside.bind(this);

      document.addEventListener(EVENT_CHANGE, this._handleStoreChange);
      document.addEventListener(EVENT_OPEN,   this._handleOpen);
      document.addEventListener(EVENT_CLOSE,  this._handleClose);
      document.addEventListener('keydown',    this._handleEscape);
      this.addEventListener('click', this._handleClickInside);

      // Render inicial (vacío) para que el panel exista en el DOM al abrir
      this._render();
    };

    Cls.prototype.disconnectedCallback = function () {
      document.removeEventListener(EVENT_CHANGE, this._handleStoreChange);
      document.removeEventListener(EVENT_OPEN,   this._handleOpen);
      document.removeEventListener(EVENT_CLOSE,  this._handleClose);
      document.removeEventListener('keydown',    this._handleEscape);
      this.removeEventListener('click', this._handleClickInside);
      // Limpiar lock de scroll
      document.documentElement.classList.remove('sich-wishlist-open');
      document.body.style.overflow = '';
    };

    Cls.prototype._handleStoreChange = function () {
      if (this._isOpen) this._render();
    };

    Cls.prototype._handleOpen  = function () { this.open(); };
    Cls.prototype._handleClose = function () { this.close(); };

    Cls.prototype.open = function () {
      if (this._isOpen) return;
      this._isOpen = true;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sich-wishlist-open');
      document.body.style.overflow = 'hidden';
      this._render();
    };

    Cls.prototype.close = function () {
      if (!this._isOpen) return;
      this._isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sich-wishlist-open');
      document.body.style.overflow = '';
    };

    Cls.prototype._handleEscape = function (e) {
      if (e.key === 'Escape' && this._isOpen) this.close();
    };

    Cls.prototype._handleClickInside = function (e) {
      // Backdrop o close button
      if (e.target.closest('[data-action="close"]')) {
        this.close();
        return;
      }
      // Remover item del wishlist
      var removeBtn = e.target.closest('[data-action="remove"]');
      if (removeBtn) {
        var handle = removeBtn.getAttribute('data-handle');
        if (handle) store.remove(handle);
        return;
      }
      // Seleccionar talle
      var sizeBtn = e.target.closest('[data-action="select-size"]');
      if (sizeBtn) {
        if (sizeBtn.disabled) return;
        var itemEl = sizeBtn.closest('.sich-wishlist-item');
        if (!itemEl) return;
        var siblings = itemEl.querySelectorAll('[data-action="select-size"]');
        for (var i = 0; i < siblings.length; i++) siblings[i].classList.remove('is-active');
        sizeBtn.classList.add('is-active');
        var newVariantId = sizeBtn.getAttribute('data-variant-id');
        var addBtn = itemEl.querySelector('[data-action="add-to-cart"]');
        if (addBtn) {
          addBtn.setAttribute('data-variant-id', newVariantId);
          addBtn.disabled = false;
          addBtn.classList.remove('is-disabled');
          addBtn.textContent = 'Agregar al carrito';
        }
        return;
      }
      // Add to cart
      var addBtn2 = e.target.closest('[data-action="add-to-cart"]');
      if (addBtn2) {
        if (addBtn2.disabled) return;
        var variantId = addBtn2.getAttribute('data-variant-id');
        if (variantId) this._addToCart(variantId, addBtn2);
        return;
      }
    };

    Cls.prototype._fetchProduct = function (handle) {
      var self = this;
      if (this._fetchCache.has(handle)) {
        return Promise.resolve(this._fetchCache.get(handle));
      }
      return fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      }).then(function (data) {
        self._fetchCache.set(handle, data);
        return data;
      }).catch(function (err) {
        if (window.console && console.warn) console.warn('SICH Wishlist: fetch failed', handle, err);
        return null;
      });
    };

    Cls.prototype._addToCart = function (variantId, btnEl) {
      btnEl.disabled = true;
      btnEl.classList.add('is-loading');
      var originalText = btnEl.textContent;
      btnEl.textContent = 'Agregando…';

      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
        credentials: 'same-origin'
      }).then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error((data && data.description) || 'Error al agregar');
          });
        }
        return res.json();
      }).then(function () {
        btnEl.classList.remove('is-loading');
        btnEl.classList.add('is-success');
        btnEl.textContent = 'Agregado ✓';

        // Sincronizar contador del carrito de Prestige
        try {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          // Prestige tiene cart-dot y cart-count que se actualizan via fetch
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var counts = document.querySelectorAll('cart-count');
              for (var i = 0; i < counts.length; i++) counts[i].textContent = String(cart.item_count);
              var dots = document.querySelectorAll('cart-dot');
              for (var j = 0; j < dots.length; j++) {
                if (cart.item_count > 0) dots[j].classList.add('is-visible');
                else dots[j].classList.remove('is-visible');
              }
            }).catch(function () { /* no-op */ });
        } catch (e) { /* no-op */ }

        setTimeout(function () {
          btnEl.classList.remove('is-success');
          btnEl.disabled = false;
          btnEl.textContent = originalText;
        }, 1500);
      }).catch(function (err) {
        btnEl.classList.remove('is-loading');
        btnEl.classList.add('is-error');
        btnEl.textContent = err.message || 'Error';
        setTimeout(function () {
          btnEl.classList.remove('is-error');
          btnEl.disabled = false;
          btnEl.textContent = originalText;
        }, 2500);
      });
    };

    Cls.prototype._render = function () {
      var panel = this.querySelector('.sich-wishlist-drawer__panel');
      if (!panel) return;
      this._renderToken += 1;
      var token = this._renderToken;

      var items = store.getAll();
      var count = items.length;

      var headerHtml = ''
        + '<header class="sich-wishlist-drawer__header">'
        +   '<h2 class="sich-wishlist-drawer__title">Favoritos' + (count > 0 ? ' (' + count + ')' : '') + '</h2>'
        +   '<button type="button" class="sich-wishlist-drawer__close" data-action="close" aria-label="Cerrar favoritos">'
        +     '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">'
        +       '<path d="M5 5L19 19M19 5L5 19" stroke-linecap="square"/>'
        +     '</svg>'
        +   '</button>'
        + '</header>';

      if (count === 0) {
        panel.innerHTML = headerHtml
          + '<div class="sich-wishlist-drawer__empty">'
          +   '<p class="sich-wishlist-drawer__empty-title">Tus favoritos están vacíos.</p>'
          +   '<p class="sich-wishlist-drawer__empty-hint">Guardá las prendas que más te gustan tocando el ícono <span aria-hidden="true">□</span> en cada producto.</p>'
          + '</div>';
        return;
      }

      // Estructura básica + skeleton mientras fetcheamos
      var skeletonHtml = '';
      for (var i = 0; i < items.length; i++) {
        skeletonHtml += '<article class="sich-wishlist-item sich-wishlist-item--loading" aria-busy="true">'
          + '<div class="sich-wishlist-item__media-skeleton"></div>'
          + '<div class="sich-wishlist-item__info">'
          +   '<div class="sich-wishlist-item__skeleton-line"></div>'
          +   '<div class="sich-wishlist-item__skeleton-line sich-wishlist-item__skeleton-line--short"></div>'
          + '</div>'
          + '</article>';
      }

      panel.innerHTML = headerHtml
        + '<div class="sich-wishlist-drawer__items">' + skeletonHtml + '</div>';

      // Fetch de los productos en paralelo
      var self = this;
      Promise.all(items.map(function (item) {
        return self._fetchProduct(item.handle).then(function (product) {
          return { item: item, product: product };
        });
      })).then(function (results) {
        // Si llegó otro render mientras tanto, abortar
        if (token !== self._renderToken) return;
        var container = panel.querySelector('.sich-wishlist-drawer__items');
        if (!container) return;

        container.innerHTML = results.map(function (r) {
          if (!r.product) return self._renderUnavailable(r.item);
          return self._renderItem(r.item, r.product);
        }).join('');
      });
    };

    Cls.prototype._renderUnavailable = function (item) {
      return ''
        + '<article class="sich-wishlist-item sich-wishlist-item--unavailable" data-handle="' + escapeHtml(item.handle) + '">'
        +   '<div class="sich-wishlist-item__info">'
        +     '<p class="sich-wishlist-item__title">Producto no disponible</p>'
        +     '<p class="sich-wishlist-item__tejido">' + escapeHtml(item.handle) + '</p>'
        +   '</div>'
        +   '<button type="button" data-action="remove" data-handle="' + escapeHtml(item.handle) + '" class="sich-wishlist-item__remove" aria-label="Quitar de favoritos">'
        +     '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">'
        +       '<path d="M5 5L19 19M19 5L5 19" stroke-linecap="square"/>'
        +     '</svg>'
        +   '</button>'
        + '</article>';
    };

    Cls.prototype._renderItem = function (item, product) {
      var handle = item.handle;
      var title = product.title || '';
      var featured = product.featured_image || (product.images && product.images[0]) || '';
      // Si la imagen es relativa (//cdn.shopify...), Shopify la sirve via protocolo del browser

      // Identificar pos del tejido y del talle en options
      var tejidoIdx = -1;
      var sizeIdx   = -1;
      var options = product.options || [];
      for (var i = 0; i < options.length; i++) {
        var name = String(options[i] || '').toLowerCase();
        if (name === 'tejido' || name === 'tela')                tejidoIdx = i;
        if (name === 'talle'  || name === 'tamaño' || name === 'tamano' || name === 'size') sizeIdx = i;
      }

      // Determinar variant seleccionado por defecto
      var selectedVariant = null;
      if (item.variantId) {
        for (var v = 0; v < product.variants.length; v++) {
          if (String(product.variants[v].id) === String(item.variantId)) {
            selectedVariant = product.variants[v];
            break;
          }
        }
      }
      // Si guardamos solo tejido (no variantId), buscar primer variant con ese tejido y stock
      if (!selectedVariant && item.tejido && tejidoIdx >= 0) {
        var tejidoKey = 'option' + (tejidoIdx + 1);
        for (var w = 0; w < product.variants.length; w++) {
          var vv = product.variants[w];
          if (vv[tejidoKey] === item.tejido && vv.available) {
            selectedVariant = vv;
            break;
          }
        }
        // Si todas las variantes de ese tejido están out-of-stock, agarrar la primera igual
        if (!selectedVariant) {
          for (var x = 0; x < product.variants.length; x++) {
            if (product.variants[x][tejidoKey] === item.tejido) {
              selectedVariant = product.variants[x];
              break;
            }
          }
        }
      }
      // Fallback: primer variant available, o el primero a secas
      if (!selectedVariant) {
        for (var y = 0; y < product.variants.length; y++) {
          if (product.variants[y].available) { selectedVariant = product.variants[y]; break; }
        }
      }
      if (!selectedVariant) selectedVariant = product.variants[0];

      // Tejido label: priorizar el guardado, sino el del variant
      var tejidoLabel = item.tejido || (tejidoIdx >= 0 ? selectedVariant['option' + (tejidoIdx + 1)] : '');
      var price = formatPriceCents(selectedVariant ? selectedVariant.price : product.price);

      // Talles: variants que matcheen el tejido seleccionado (si aplica)
      var sizeButtons = '';
      if (sizeIdx >= 0) {
        var sizeKey = 'option' + (sizeIdx + 1);
        var tejidoKey2 = tejidoIdx >= 0 ? 'option' + (tejidoIdx + 1) : null;
        var matchingVariants = product.variants.filter(function (vv) {
          if (!tejidoKey2 || !tejidoLabel) return true;
          return vv[tejidoKey2] === tejidoLabel;
        });

        sizeButtons = '<div class="sich-wishlist-item__sizes" role="radiogroup" aria-label="Talle">';
        for (var s = 0; s < matchingVariants.length; s++) {
          var mv = matchingVariants[s];
          var sizeName = mv[sizeKey] || '';
          var isSelected = selectedVariant && selectedVariant.id === mv.id;
          var available = !!mv.available;
          sizeButtons += '<button'
            + ' type="button"'
            + ' role="radio"'
            + ' aria-checked="' + (isSelected ? 'true' : 'false') + '"'
            + ' data-action="select-size"'
            + ' data-variant-id="' + mv.id + '"'
            + ' class="sich-wishlist-item__size'
            +   (isSelected ? ' is-active' : '')
            +   (!available ? ' is-unavailable' : '') + '"'
            + (!available ? ' disabled aria-label="Talle ' + escapeHtml(sizeName) + ' agotado"' : ' aria-label="Talle ' + escapeHtml(sizeName) + '"')
            + '>' + escapeHtml(sizeName) + '</button>';
        }
        sizeButtons += '</div>';
      }

      var canAdd = !!(selectedVariant && selectedVariant.available);
      var addText = canAdd ? 'Agregar al carrito' : 'Sin stock';
      var addAttrs = canAdd ? '' : ' disabled';

      return ''
        + '<article class="sich-wishlist-item" data-handle="' + escapeHtml(handle) + '">'
        +   '<a href="/products/' + encodeURIComponent(handle) + '" class="sich-wishlist-item__media" aria-label="' + escapeHtml(title) + '">'
        +     (featured ? '<img src="' + escapeHtml(featured) + '" alt="' + escapeHtml(title) + '" loading="lazy">' : '')
        +   '</a>'
        +   '<div class="sich-wishlist-item__info">'
        +     '<div class="sich-wishlist-item__top">'
        +       '<div class="sich-wishlist-item__title-block">'
        +         '<a href="/products/' + encodeURIComponent(handle) + '" class="sich-wishlist-item__title">' + escapeHtml(title) + '</a>'
        +         (tejidoLabel ? '<p class="sich-wishlist-item__tejido">' + escapeHtml(tejidoLabel) + '</p>' : '')
        +       '</div>'
        +       '<button type="button" data-action="remove" data-handle="' + escapeHtml(handle) + '" class="sich-wishlist-item__remove" aria-label="Quitar de favoritos">'
        +         '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">'
        +           '<path d="M5 5L19 19M19 5L5 19" stroke-linecap="square"/>'
        +         '</svg>'
        +       '</button>'
        +     '</div>'
        +     '<p class="sich-wishlist-item__price">' + escapeHtml(price) + '</p>'
        +     sizeButtons
        +     '<button type="button" data-action="add-to-cart" data-variant-id="' + (selectedVariant ? selectedVariant.id : '') + '" class="sich-wishlist-item__add"' + addAttrs + '>' + addText + '</button>'
        +   '</div>'
        + '</article>';
    };

    return Cls;
  }();

  /* --------------------------------------------------------------------------
   * Registro de custom elements
   * ------------------------------------------------------------------------ */
  if (!customElements.get('sich-wishlist-bookmark')) {
    customElements.define('sich-wishlist-bookmark', SichWishlistBookmark);
  }
  if (!customElements.get('sich-wishlist-trigger')) {
    customElements.define('sich-wishlist-trigger', SichWishlistTrigger);
  }
  if (!customElements.get('sich-wishlist-drawer')) {
    customElements.define('sich-wishlist-drawer', SichWishlistDrawer);
  }
})();
